import React, { useState } from 'react'

import styled from 'styled-components'
import Modal from 'components/lib/Modal'
import Grid from 'components/lib/Grid'
import Button from 'components/lib/Button'

import withRedux from 'components/redux'
import * as common from 'utils/UtilityStore/actions'

import { cnpjValidation } from 'utils/cnpjValidation'
import { cpfValidation } from 'utils/cpfValidation'

import {
  FormFieldSelect,
  FormFieldText,
  FormFieldTextArea,
} from 'components/lib/FormFields'

import { request } from 'core/constants'

import Field from 'components/lib/FormFields/components/Field'
import Control from 'components/lib/FormFields/components/Control'

const Subtitle = styled.p`
  font-weight: bold;
  width: 100%;
  color: #5f6775;
`

const Divider = styled.hr`
  width: 100%;
  border: 0;
  opacity: 0.5;
  margin-top: 10px;
  margin-bottom: 10px;
`

const Description = styled.p`
  color: #6b6f77;
`

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  & > span {
    color: #ef4444;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`

const LoadingContainer = styled.div`
  height: 250px;
  margin-top: 100px;
`

const Spinner = styled.div`
  margin: 0 auto;
  padding: 45px 60px;
`

const CreateExtraBillingModal = ({
  isOpen,
  onClose,
  onCreated,
  user,
  companyId,
  proposalId,
  clients,
  notification,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedClient, setSelectedClient] = useState(clients[0])
  const [email, setEmail] = useState(clients[0]?.email)
  const [paymentForm, setPaymentForm] = useState('PIX')
  const [errors, setErrors] = useState({
    client: '',
    email: '',
    price: '',
    description: '',
  })

  const [price, setPrice] = useState('')
  const [description, setDescription] = useState('')
  const [receiptPixKey, setReceiptPixKey] = useState('')

  const clearStateAndClose = () => {
    setErrors({ client: '', email: '', price: '', description: '' })
    setEmail('')
    setSelectedClient(clients[0])
    setPrice('')
    setDescription('')
    setReceiptPixKey('')
    onClose()
  }

  const validatePrice = (value) => {
    const priceNum = parseFloat(value)

    if (!priceNum || priceNum <= 100) {
      setErrors((prev) => ({
        ...prev,
        price: 'O valor deve ser maior do que R$ 100,00.',
      }))
    } else {
      setErrors((prev) => ({ ...prev, price: '' }))
    }
  }

  const validateClient = (client) => {
    if (!client || !client.name || client.name.trim().split(' ').length < 2) {
      setErrors((prev) => ({
        ...prev,
        client: 'O nome do pretendente deve conter mais de uma palavra.',
      }))
    } else if (
      !cpfValidation(client.cpf?.replace(/\D/g, '') || '') &&
      !cnpjValidation(client.cnpj || '')
    ) {
      setErrors((prev) => ({
        ...prev,
        client: 'O cpf/cnpj do pretendente deve ser válido.',
      }))
    } else if (
      !client.address?.zip_code ||
      !client.address?.state ||
      !client.address?.address ||
      !client.address?.number ||
      !client.address?.city ||
      !client.address?.neighborhood
    ) {
      setErrors((prev) => ({
        ...prev,
        client: 'O endereço do pretendente deve estar completo.',
      }))
    } else {
      setErrors((prev) => ({ ...prev, client: '' }))
    }
  }

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!value || !emailRegex.test(value)) {
      setErrors((prev) => ({
        ...prev,
        email: 'Insira um email válido.',
      }))
    } else {
      setErrors((prev) => ({ ...prev, email: '' }))
    }
  }

  const validateDescription = (value) => {
    const lines = value.split('\n')
    const invalid = lines.length > 3 || lines.some((line) => line.length > 100)

    if (invalid) {
      setErrors((prev) => ({
        ...prev,
        description:
          'A descrição deve ter no máximo 3 linhas, com até 100 caracteres cada.',
      }))
    } else {
      setErrors((prev) => ({ ...prev, description: '' }))
    }
  }

  const submit = async () => {
    try {
      setIsLoading(true)

      const response = await request.post(
        `/company/${companyId}/proposal/${proposalId}/extra-billing`,
        {
          client_id: selectedClient.id,
          billing_email: email,
          payment_form: paymentForm,
          description: description,
          price: parseFloat(price),
          recipient_pix_key: receiptPixKey,
        },
      )

      notification({
        message: 'Cobrança adicional cadastrada com sucesso!',
      })

      setIsLoading(false)

      onCreated(response.data)
    } catch (err) {
      setIsLoading(false)

      notification({
        message: err?.message ? err.message : 'Erro ao cadastrar cobrança adicional',
        status: err ? 'warning' : 'danger',
      })
    }
  }

  if (!isOpen) {
    return null
  }

  if (isLoading) {
    return (
      <Modal title='Envio de cobrança' isOpen={isOpen} onClose={onClose}>
        <div>
          <LoadingContainer>
            <Spinner className='empty-state-spinner' />
          </LoadingContainer>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title='Envio de cobrança' isOpen={isOpen} onClose={clearStateAndClose}>
      <Subtitle>
        DADOS DO PRETENDENTE
        <Divider />
      </Subtitle>
      <Grid>
        <Grid.Col>
          <Label>
            Selecione o Pretendente para receber a cobrança <span>*</span>
          </Label>
          <Field>
            <Control>
              <FormFieldSelect
                valueKey='id'
                hideBlankOption
                valueDefault={clients[0]?.id}
                options={clients.map((client) => ({
                  id: client.id,
                  name: client.name,
                }))}
                isDisabled={false}
                forceInvalid={!!errors.client}
                errorMessage={errors.client}
                onChange={(value) => {
                  const client = clients.find((c) => c.id === value)

                  validateClient(client)
                  setSelectedClient(client)
                  setEmail(client.email)
                }}
              />
            </Control>
          </Field>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <Label>
            Email de cobrança <span>*</span>
          </Label>
          <Field>
            <Control>
              <FormFieldText
                type='email'
                forceInvalid={!!errors.email}
                errorMessage={errors.email}
                onChange={(value) => {
                  setEmail(value)
                  validateEmail(value)
                }}
                placeholder='Digite o email que receberá a cobrança'
                valueDefault={user.email}
              />
            </Control>
          </Field>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <Label>
            Valor da cobrança <span>*</span>
          </Label>
          <Field>
            <Control>
              <FormFieldText
                valueDefault={price}
                onChange={(value) => {
                  setPrice(value)
                  validatePrice(value)
                }}
                type='number'
                forceInvalid={!!errors.price}
                errorMessage={errors.price}
              />
            </Control>
          </Field>
        </Grid.Col>
        <Grid.Col>
          <Label>
            Forma de pagamento <span>*</span>
          </Label>
          <Field>
            <Control>
              <FormFieldSelect
                valueKey='id'
                valueDefault='PIX'
                hideBlankOption
                options={[
                  { id: 'PIX', name: 'PIX' },
                  { id: 'TICKET', name: 'BOLETO' },
                ]}
                isDisabled={false}
                onChange={setPaymentForm}
              />
            </Control>
          </Field>
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col>
          <Label>Descrição</Label>
          <Field>
            <Control>
              <FormFieldTextArea
                multiline
                rows={3}
                forceInvalid={!!errors.description}
                errorMessage={errors.description}
                maxLength={300}
                valueDefault={description}
                onChange={(value) => {
                  setDescription(value)
                  validateDescription(value)
                }}
              />
            </Control>
          </Field>
        </Grid.Col>
      </Grid>

      <Subtitle>
        DADOS DA IMOBILIÁRIA
        <Divider />
      </Subtitle>
      <Description>
        Informe a seguir os dados bancários para recebimento via PIX do valor pago
        pelo Pretendente(inquilino).
      </Description>
      <Grid>
        <Grid.Col>
          <Label>
            Chave PIX para recebimento <span>*</span>
          </Label>
          <Field>
            <Control>
              <FormFieldText
                valueDefault={receiptPixKey}
                onChange={setReceiptPixKey}
              />
            </Control>
          </Field>
        </Grid.Col>
      </Grid>

      <ButtonGroup>
        <Button isBare onClick={clearStateAndClose}>
          Cancelar
        </Button>
        <Button
          hasThemeColor
          isLoading={false}
          isDisabled={
            errors.email ||
            errors.client ||
            errors.price ||
            errors.description ||
            !receiptPixKey
          }
          onClick={submit}
        >
          Finalizar
        </Button>
      </ButtonGroup>
    </Modal>
  )
}

export default withRedux(
  (store) => ({
    user: store.app.activeUser,
  }),
  { ...common },
)(CreateExtraBillingModal)
